import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import LocationOnIcon from '@material-ui/icons/LocationOn'
import Autocomplete from '@material-ui/lab/Autocomplete'
import parse from 'autosuggest-highlight/parse'
import { find, get } from 'lodash'
import throttle from 'lodash/throttle'
import { useEffect, useMemo, useState } from 'react'
import { useNotify } from 'react-admin'
import { useDispatch, useSelector } from 'react-redux'

import { captureExceptionIfProduction } from 'helpers/utils'
import { getSettings } from '../../api/core'
import { GOOGLE_APIS_LOADED } from '../../helpers/constants'

function getPlaceDetails(placeId: any) {
  // given a Google placeId, return some details about the place (w/ details specified by 'fields' property of request)
  // (to see possible fields, see https://developers.google.com/places/web-service/details#fields)
  var request = {
    placeId,
    fields: ['address_component'],
  }

  const service = new window.google.maps.places.PlacesService(document.createElement('div'))

  return new Promise((resolve, reject) => {
    service.getDetails(request, (results, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        resolve(results)
      }
      reject(status)
    })
  })
}

function loadScript(src: string, position: any, id: string) {
  if (!position) {
    return
  }

  const script = document.createElement('script')
  script.setAttribute('async', '')
  script.setAttribute('id', id)
  script.src = src
  position.appendChild(script)
}

const autocompleteService: any = { current: null }

/**
 * AddressAutoComplete renders a text input that suggests addresses as you start typing.
 * Tapping on a suggested address will decompose the address into its components (street, city, state, zip)
 * Pass in functions (e.g. setCity, setState, etc. ) to props to update state elsewhere
 */
export default function AddressAutoComplete({ label, setLine1, setCity, setState, setZip }: any) {
  const [value, setValue] = useState(null) // the currently selected value
  const [inputValue, setInputValue] = useState('') // whatever the user has currently typed into input
  const [options, setOptions] = useState<any>([]) // array of possible addresses to autocomplete given user's inputValue

  const selectedPlaceId = get(value, 'place_id') // google identifier for a place

  const googleApisLoaded = useSelector((state: any) => state.googleApi.googleApisLoaded)

  const dispatch = useDispatch()
  const notify = useNotify()

  useEffect(() => {
    async function loadGooglePlacesAPI() {
      try {
        const settings = await getSettings()
        const googleApiKey = get(settings, 'web_google_api_key')
        if (googleApiKey) {
          const url = `https://maps.googleapis.com/maps/api/js?key=${googleApiKey}&libraries=places`
          loadScript(url, document.querySelector('head'), 'google-maps')
          dispatch({ type: GOOGLE_APIS_LOADED })
        }
      } catch (error) {
        console.error('Error loading Google Places API', error)
        captureExceptionIfProduction(error)
      }
    }

    if (!googleApisLoaded) {
      loadGooglePlacesAPI()
    }
  }, [googleApisLoaded, dispatch, notify])

  // given addressComponents from google response, returns { street, city, state, zip }
  const decomposeAddressComponents = (addressComponents: any) => {
    const streetNumberComponent = find(
      addressComponents,
      (component) => component.types[0] === 'street_number'
    )
    const routeComponent = find(addressComponents, (component) => component.types[0] === 'route')
    const cityComponent = find(addressComponents, (component) => component.types[0] === 'locality')
    const stateComponent = find(
      addressComponents,
      (component) => component.types[0] === 'administrative_area_level_1'
    )
    const zipComponent = find(addressComponents, (component) => component.types[0] === 'postal_code')

    return {
      street: get(streetNumberComponent, 'long_name') + ' ' + get(routeComponent, 'long_name'),
      city: get(cityComponent, 'long_name'),
      state: get(stateComponent, 'short_name'), // TODO prop to configure long / short?
      zip: get(zipComponent, 'short_name'),
    }
  }

  useEffect(() => {
    async function updateAddressComponents(selectedPlaceId: any) {
      try {
        const details = await getPlaceDetails(selectedPlaceId)
        const addressComponents = get(details, 'address_components')
        // TODO optionally, country?
        const { street, city, state, zip } = decomposeAddressComponents(addressComponents)

        // use passed in functions from props to update address components
        setLine1 && setLine1(street)
        setCity && setCity(city)
        setState && setState(state)
        setZip && setZip(zip)
      } catch (error) {
        notify('Google Place Details / Address Autocomplete Failed', {
          type: 'error',
        })
        captureExceptionIfProduction(error)
      }
    }

    // when selected place changes, update address components (street, city, state, etc.)
    if (selectedPlaceId) {
      updateAddressComponents(selectedPlaceId)
    }
  }, [selectedPlaceId, setLine1, setCity, setState, setZip, notify])

  const fetch = useMemo(
    () =>
      throttle((request, callback) => {
        autocompleteService.current.getPlacePredictions(request, callback)
      }, 800),
    []
  )

  useEffect(() => {
    let active = true

    if (!autocompleteService.current && window.google) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService()
    }
    if (!autocompleteService.current) {
      return undefined
    }

    if (inputValue === '') {
      setOptions(value ? [value] : [])
      return undefined
    }

    const autocompleteRequest = { input: inputValue }
    fetch(autocompleteRequest, (results: any) => {
      if (active) {
        let newOptions: any = []

        if (value) {
          newOptions = [value]
        }

        if (results) {
          newOptions = [...newOptions, ...results]
        }

        setOptions(newOptions)
      }
    })

    return () => {
      active = false
    }
  }, [value, inputValue, fetch])

  return (
    <Autocomplete
      id="address-auto-complete"
      getOptionLabel={(option: any) => (typeof option === 'string' ? option : option.description)}
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={value}
      onChange={(_, newValue: any) => {
        // user has selected / tapped an address
        setOptions(newValue ? [newValue, ...options] : options)
        setValue(newValue)
      }}
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      renderInput={(params) => <TextField {...params} label={label} variant="outlined" fullWidth />}
      renderOption={(option) => {
        const matches = option.structured_formatting.main_text_matched_substrings
        const parts = parse(
          option.structured_formatting.main_text,
          matches.map((match: any) => [match.offset, match.offset + match.length])
        )

        return (
          <Grid container alignItems="center">
            <Grid item>
              <Box mr={2}>
                <LocationOnIcon />
              </Box>
            </Grid>
            <Grid item xs>
              {parts.map((part: any, index: any) => (
                <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                  {part.text}
                </span>
              ))}

              <Typography variant="body2" color="textSecondary">
                {option.structured_formatting.secondary_text}
              </Typography>
            </Grid>
          </Grid>
        )
      }}
    />
  )
}
